import type {
  SessionInfo, TeamInfo, Dashboard, ActivityCatalogEntry,
  Decision, ValidationResponse, LeaderboardResponse,
  EventInfo, ScenarioInfo, WSMessage, SessionHistoryResponse,
  AIConfigResponse, AITeamConfig, PersonasResponse,
  User, OwnedSessionInfo,
} from '../types'

const BASE = ''  // same-origin; Vite proxy handles dev

// All API calls share `credentials: 'include'` so the auth cookie is sent.
// Required for `/api/auth/*` and any endpoint that reads `current_user`.
function f(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, { credentials: 'include', ...init })
}

async function j<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    let detail: unknown
    try { detail = await resp.json() } catch { detail = await resp.text() }
    throw new ApiError(resp.status, detail)
  }
  return resp.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown) {
    super(typeof detail === 'string' ? detail :
      (detail as { detail?: string })?.detail ?? `HTTP ${status}`)
    this.name = 'ApiError'
  }
}

// --- Catalog ---------------------------------------------------------------

export const getActivitiesCatalog = () =>
  f(`${BASE}/api/activities`).then(j<Array<{ id: string, name: string, category: string }>>)

export const getEventsCatalog = () =>
  f(`${BASE}/api/events`).then(j<EventInfo[]>)

export const getScenarios = () =>
  f(`${BASE}/api/scenarios`).then(j<ScenarioInfo[]>)

export const getAIConfig = () =>
  f(`${BASE}/api/ai-config`).then(j<AIConfigResponse>)

export const getAIPersonas = () =>
  f(`${BASE}/api/ai-personas`).then(j<PersonasResponse>)

// --- Session lifecycle ----------------------------------------------------

export const createSession = (body: {
  name: string
  num_quarters: number
  competitive_dynamics_mode: boolean
  scenario: string
  ai_teams?: AITeamConfig[]
  minutes_per_quarter?: number | null
}) =>
  f(`${BASE}/api/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(j<SessionInfo>)

export const getSession = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}`).then(j<SessionInfo>)

export const listSessions = () =>
  f(`${BASE}/api/sessions`).then(j<SessionInfo[]>)

export const joinTeam = (sessionId: string, teamName: string) =>
  f(`${BASE}/api/sessions/${sessionId}/teams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ team_name: teamName }),
  }).then(j<TeamInfo>)

export const startSession = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/start`, { method: 'POST' }).then(j<SessionInfo>)

export const finishSession = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/finish`, { method: 'POST' }).then(j<SessionInfo>)

export const pauseSession = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/pause`, { method: 'POST' }).then(j<SessionInfo>)

export const resumeSession = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/resume`, { method: 'POST' }).then(j<SessionInfo>)

export const adjustTimer = (sessionId: string, deltaSeconds: number) =>
  f(`${BASE}/api/sessions/${sessionId}/timer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ delta_seconds: deltaSeconds }),
  }).then(j<SessionInfo>)

export async function deleteSession(sessionId: string): Promise<void> {
  const resp = await f(`${BASE}/api/sessions/${sessionId}`, { method: 'DELETE' })
  if (!resp.ok) throw new ApiError(resp.status, await resp.text())
}

// --- Team / gameplay ------------------------------------------------------

export const getDashboard = (sessionId: string, teamId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/teams/${teamId}/dashboard`).then(j<Dashboard>)

export const getTeamCatalog = (sessionId: string, teamId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/teams/${teamId}/catalog`).then(j<ActivityCatalogEntry[]>)

export const submitDecision = (sessionId: string, teamId: string, decision: Decision) =>
  f(`${BASE}/api/sessions/${sessionId}/teams/${teamId}/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(decision),
  }).then(j<ValidationResponse>)

export const advanceQuarter = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/advance`, { method: 'POST' }).then(j<{
    session_id: string
    quarter: number
    team_results: Record<string, import('../types').TurnResult>
  }>)

export const getLeaderboard = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/leaderboard`).then(j<LeaderboardResponse>)

export const getSessionEvents = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/events`).then(j<Array<{
    quarter: number, team_id: string, events: string[]
  }>>)

export const getSessionHistory = (sessionId: string) =>
  f(`${BASE}/api/sessions/${sessionId}/history`).then(j<SessionHistoryResponse>)

// --- Auth -----------------------------------------------------------------

export async function getMe(): Promise<User | null> {
  const resp = await f(`${BASE}/api/auth/me`)
  if (resp.status === 401) return null
  if (!resp.ok) throw new ApiError(resp.status, await resp.text())
  const body = await resp.json() as { user: User }
  return body.user
}

export const logout = () =>
  f(`${BASE}/api/auth/logout`, { method: 'POST' }).then(j<{ ok: boolean }>)

export const listMySessions = (limit?: number) => {
  const url = `${BASE}/api/users/me/sessions`
    + (limit !== undefined ? `?limit=${limit}` : '')
  return f(url).then(j<OwnedSessionInfo[]>)
}

/** Build the URL for an OAuth login redirect. The browser navigates to it
 * directly (full page nav, not fetch). */
export function loginUrl(provider: 'google' | 'microsoft', next?: string): string {
  const q = next ? `?next=${encodeURIComponent(next)}` : ''
  return `${BASE}/api/auth/${provider}/login${q}`
}

// --- WebSocket ------------------------------------------------------------

export function openTeamWebSocket(
  sessionId: string, teamId: string,
  onMessage: (msg: WSMessage) => void,
) {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const ws = new WebSocket(`${proto}://${window.location.host}/ws/${sessionId}/${teamId}`)
  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data) as WSMessage
      onMessage(msg)
    } catch {
      // ignore malformed frames
    }
  }
  return ws
}
